import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Routes, Route, Link } from 'react-router-dom';
import StylingEngine from './components/StylingEngine';
import WardrobeManager from './components/WardrobeManager';
import { StylingProvider, useStyling } from './context/StylingContext';
import SubscriptionManager from './components/SubscriptionManager';
import PaymentSimulation from './components/PaymentSimulation';
import IdentityVault from './components/IdentityVault';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import Dashboard from './components/Dashboard';
import FeedbackModal from './components/FeedbackModal';
import { useToasts } from './components/TactileToasts';
import { useConfirm } from './context/ConfirmContext';
import { SubscriptionTier } from './types/synclothe';
import { clear } from 'idb-keyval';
import ProviderConflictModal from './components/ProviderConflictModal';
import EnvironmentBanner from './components/EnvironmentBanner';
import CookieBanner from './components/CookieBanner';
import SubscriptionAuthGate from './components/SubscriptionAuthGate';

const DkpmoBrandLink: React.FC = () => {
    const [showContactModal, setShowContactModal] = useState(false);

    return (
        <span className="relative inline-block ml-1">
            <button
                type="button"
                onClick={() => setShowContactModal(true)}
                className="font-montserrat font-black text-white tracking-tighter text-[12px] hover:text-azure-400 cursor-pointer transition-colors focus:outline-none"
            >
                DKPMO.
            </button>

            {/* Click / Tap Touch Modal (Rendered via Portal at Document Body Level for 100% Solid Opacity) */}
            {showContactModal && createPortal(
                <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-[99999] p-4 animate-fade-in pointer-events-auto opacity-100">
                    <div className="bg-[#05070c] border-2 border-azure-500 p-6 rounded-2xl max-w-sm w-full text-center space-y-4 shadow-[0_0_80px_rgba(0,0,0,1)] opacity-100 text-white">
                        <div className="w-12 h-12 bg-azure/20 rounded-full flex items-center justify-center mx-auto border border-azure/40">
                            <svg className="w-6 h-6 text-azure-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5m0 0h4m-4 0a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                            </svg>
                        </div>
                        
                        <div>
                            <h3 className="text-sm font-black text-white uppercase tracking-wider">DKPMO STRATEGY CONSULTANTS LLP</h3>
                            <p className="text-[10px] text-gray-200 font-mono mt-1 font-bold">ACV-3415 | 06AAZFD4197J1ZN</p>
                            <p className="text-[9px] text-gray-300 font-mono mt-1 leading-relaxed">G-803, Maple Heights C-Block Sushant Lok, Phase-I Gurugram Haryana 122009</p>
                        </div>

                        <div className="p-4 bg-[#0f172a] border-2 border-azure-400/80 rounded-xl space-y-1.5 shadow-xl">
                            <p className="text-xs font-black text-azure-300 tracking-wider">Write to : contact@dkpmo.com</p>
                            <p className="text-xs font-black text-azure-300 tracking-wider">Contact number : 91-124-4045174</p>
                        </div>

                        <a 
                            href="mailto:contact@dkpmo.com" 
                            className="btn-azure text-xs font-black uppercase tracking-widest py-2.5 px-6 rounded-xl inline-block w-full shadow-lg"
                        >
                            Open Mail Client
                        </a>

                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowContactModal(false);
                            }}
                            className="text-xs text-gray-300 hover:text-white uppercase font-bold tracking-widest pt-2 block mx-auto cursor-pointer"
                        >
                            Close
                        </button>
                    </div>
                </div>,
                document.body
            )}
        </span>
    );
};

const BackgroundGenerationIndicator: React.FC = () => {
    const { isProcessing } = useStyling();
    if (!isProcessing) return null;

    return (
        <Link 
            to="/" 
            className="flex items-center gap-2 px-3 py-1 bg-azure-500/10 border border-azure-500/30 rounded-full animate-pulse text-[9px] font-black uppercase tracking-widest text-azure-400 hover:bg-azure-500/20 transition-all cursor-pointer shadow-[0_0_15px_rgba(0,180,216,0.2)]"
            title="Style generation running in background. Click to return to Engine."
        >
            <span className="w-1.5 h-1.5 rounded-full bg-azure-400 animate-ping"></span>
            <span className="hidden lg:inline">Orchestrating in Background...</span>
            <span className="lg:hidden">Orchestrating...</span>
        </Link>
    );
};

const MobileEngineIndicator: React.FC = () => {
    const { isProcessing } = useStyling();
    if (!isProcessing) return null;

    return (
        <span className="absolute -top-1 right-2 flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-azure-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-azure-500"></span>
        </span>
    );
};

function App() {
  const { showToast } = useToasts();
  const { confirm } = useConfirm();
  const [isInitializing, setIsInitializing] = useState(true);
  const [subscriptionTier, setSubscriptionTier] = useState<SubscriptionTier>('Explorer'); 
  const [credits, setCredits] = useState(0);
  const [expiryDate, setExpiryDate] = useState<string | null>(null);
  const [gracePeriodDaysRemaining, setGracePeriodDaysRemaining] = useState<number | undefined>(undefined);
  const [isSoftLocked, setIsSoftLocked] = useState<boolean>(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [user, setUser] = useState<{ userId: string, userDetails: string, email?: string, persona?: any, nextCycleTier?: SubscriptionTier } | null>(null);
  const [conflictData, setConflictData] = useState<{ registeredProvider: string; email: string } | null>(null);
  const [showIdentityVault, setShowIdentityVault] = useState(false);
  const [vaultInitialStep, setVaultInitialStep] = useState<'capture' | 'review'>('capture');
  const [isExportingData, setIsExportingData] = useState(false);
  const lastSyncTimeRef = React.useRef<number>(Date.now());
  const isTerminatingRef = React.useRef<boolean>(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const refreshUser = React.useCallback(async (isBackgroundSync = false) => {
    if (isTerminatingRef.current) return;
    try {
      const response = await fetch('/api/me');
      const data = await response.json();
      
      if (response.status === 409 || data.conflict) {
          setConflictData({
              registeredProvider: data.registeredProvider || 'github',
              email: data.email || ''
          });
          setUser(null);
          setSubscriptionTier('Explorer');
          return;
      }

      if (data.authenticated) {
          setConflictData(null);
          setUser({ userId: data.userId, userDetails: data.userDetails, email: data.email, persona: data.persona, nextCycleTier: data.nextCycleTier });
          setSubscriptionTier(data.tier);
          setCredits(data.credits);
          setExpiryDate(data.expiryDate);
          setGracePeriodDaysRemaining(data.gracePeriodDaysRemaining);
          setIsSoftLocked(!!data.isSoftLocked);

          // Trigger Identity Vault auto-popup ONLY for paid tiers (Standard/Premium)
          if (!data.persona?.description && data.tier !== 'Explorer' && !isBackgroundSync) {
              setShowIdentityVault(true);
          }
      } else {
          setConflictData(null);
          // If session expired while away
          if (user && isBackgroundSync) {
              setUser(null);
              setSubscriptionTier('Explorer');
              showToast("Session expired. Please log in again.", "logic");
          }
      }
      lastSyncTimeRef.current = Date.now();
    } catch (e) { 
      console.error("System sync fail"); 
    } finally {
      setIsInitializing(false);
    }
  }, [user, showToast]);

  useEffect(() => {
    refreshUser();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const elapsed = Date.now() - lastSyncTimeRef.current;
        // Debounce: sync if more than 15 seconds have passed since last sync
        if (elapsed > 15000) {
          refreshUser(true);
        }
      }
    };

    const handleOnline = () => {
      refreshUser(true);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [refreshUser]);

  const handlePaymentSuccess = (newTier: string) => {
    if (newTier !== 'current') setSubscriptionTier(newTier as SubscriptionTier);
    // Refresh credits after payment
    fetch('/api/me').then(res => res.json()).then(data => setCredits(data.credits));
  };

  const handleLogout = async () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      await clear();
    } catch (e) {
      console.error("Storage clear on logout failed", e);
    }
    try {
      await fetch('/.auth/logout', { method: 'GET', mode: 'no-cors', credentials: 'same-origin' });
    } catch (e) {}
    try {
      await fetch('/.auth/logout/complete', { method: 'GET', mode: 'no-cors', credentials: 'same-origin' });
    } catch (e) {}
    setUser(null);
    window.location.replace('/');
  };

  const handleDeleteAccount = async () => {
    let warningMessage = "SECURITY WARNING: This will permanently purge your user record, digital closet, and all identity photos from the server. This action cannot be reversed.";
    
    if (subscriptionTier !== 'Explorer') {
        const dateStr = expiryDate ? new Date(expiryDate).toLocaleDateString() : 'the end of your cycle';
        warningMessage += `\n\nFINANCIAL WARNING: You are currently on an active ${subscriptionTier} plan valid until ${dateStr}. You will lose access to all remaining benefits immediately, and no refunds will be issued.`;
    } else if (credits > 0) {
        warningMessage += `\n\nFINANCIAL WARNING: You have ${credits} unused Intelligence Credits. These will be permanently deleted without refund.`;
    }

    warningMessage += "\n\nProceed with account termination?";

    const isConfirmed = await confirm("Delete Account Data?", warningMessage);
    if (isConfirmed) {
      isTerminatingRef.current = true;
      setIsInitializing(true);
      try {
        const response = await fetch('/api/deleteAccount', { method: 'POST' });
        if (response.ok) {
            try {
                await clear();
                localStorage.clear();
                sessionStorage.clear();
            } catch (storageErr) {}
            try {
                await fetch('/.auth/logout', { method: 'GET', mode: 'no-cors', credentials: 'same-origin' });
            } catch (logoutErr) {}
            try {
                await fetch('/.auth/logout/complete', { method: 'GET', mode: 'no-cors', credentials: 'same-origin' });
            } catch (logoutCompleteErr) {}
            setUser(null);
            window.location.replace('/');
        } else {
            isTerminatingRef.current = false;
            setIsInitializing(false);
            showToast("Account deletion failed. Please try again.", "error");
        }
      } catch (e) {
        isTerminatingRef.current = false;
        setIsInitializing(false);
        showToast("Network error during deletion.", "error");
      }
    }
  };

  const handleExportData = async () => {
    if (isExportingData) return;
    setIsExportingData(true);
    showToast("Generating your personal data archive (GDPR / DPDP)...", "sync");
    try {
      const response = await fetch('/api/exportUserData');
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const disposition = response.headers.get('content-disposition');
        let filename = `synclothe-personal-data-${new Date().toISOString().split('T')[0]}.json`;
        if (disposition && disposition.indexOf('filename=') !== -1) {
          const matches = /filename="([^"]+)"/.exec(disposition);
          if (matches && matches[1]) filename = matches[1];
        }
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        showToast("Personal data archive downloaded successfully.", "success");
      } else {
        const errData = await response.json().catch(() => ({}));
        showToast(errData.error || "Failed to export personal data.", "error");
      }
    } catch (err) {
      showToast("Network error while generating data archive.", "error");
    } finally {
      setIsExportingData(false);
    }
  };

  if (isInitializing) {
    return (
        <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center gap-6 animate-fade-in">
            <div className="relative">
                <div className="w-16 h-16 border-2 border-azure/20 border-t-azure rounded-full animate-spin"></div>
                <div className="absolute inset-0 bg-azure/10 blur-2xl rounded-full"></div>
            </div>
            <div className="text-center space-y-2">
                <p className="text-[10px] font-black text-white uppercase tracking-[0.5em] animate-pulse">Initializing Sync Engine</p>
                <p className="text-[8px] font-bold text-gray-600 uppercase tracking-widest">Verifying Identity Integrity...</p>
            </div>
        </div>
    );
  }

  return (
    <>
      <FeedbackModal 
        isOpen={showFeedbackModal} 
        onClose={() => setShowFeedbackModal(false)} 
        userId={user?.userId} 
      />
      {showIdentityVault && (
            <IdentityVault 
                existingPersona={user?.persona}
                initialStep={vaultInitialStep}
                onComplete={() => { setShowIdentityVault(false); refreshUser(); }} 
                onSkip={() => setShowIdentityVault(false)} 
            />
        )}
      <ProviderConflictModal
        isOpen={!!conflictData}
        registeredProvider={conflictData?.registeredProvider || 'github'}
        email={conflictData?.email || ''}
        onClose={() => setConflictData(null)}
      />
      <div className="flex flex-col min-h-screen bg-[#0a0a0a] font-inter pb-20 md:pb-0">
        <EnvironmentBanner />
        <StylingProvider subscriptionTier={subscriptionTier as any} persona={user?.persona} userId={user?.userId} onCreditUpdate={setCredits}>
        {/* Top Bar with Glassmorphism and Montserrat Logo */}
        <div className="p-4 bg-dk-900/80 backdrop-blur-md sticky top-0 z-50 border-b border-white/5 flex justify-between items-center px-4 md:px-8 shadow-2xl shadow-black/50">
          <div className="flex items-center space-x-4 md:space-x-8">
            <Link to="/" className="font-montserrat font-black uppercase tracking-tighter text-white text-lg md:text-2xl group flex items-center cursor-pointer">
              <img src="/icon.svg" alt="Synclothe Icon" className="w-6 h-6 md:w-8 md:h-8 mr-2 group-hover:scale-110 transition-transform duration-500" />
              SYNC<span className="text-azure-500">LOTHE</span>
              <div className="w-1 h-1 md:w-1.5 md:h-1.5 bg-gold-500 rounded-full ml-1 animate-pulse"></div>
            </Link>
            <nav className="hidden md:flex items-center space-x-6 animate-fade-in">
              <Link to="/" className="text-gray-400 hover:text-white text-xs font-bold uppercase tracking-widest transition-colors duration-300">Styling Engine</Link>
              {user && (
                <Link to="/subscription" className="text-gray-400 hover:text-white text-xs font-bold uppercase tracking-widest transition-colors duration-300 flex items-center gap-1.5">
                  <span>Pricing & Plans</span>
                  <span className="text-[8px] bg-azure-500/20 text-azure-400 px-1.5 py-0.2 rounded font-mono font-bold">LIVE</span>
                </Link>
              )}
              {user && <Link to="/wardrobe" className="text-gray-400 hover:text-white text-xs font-bold uppercase tracking-widest transition-colors duration-300">My Closet</Link>}
              <Link to="/dashboard" className="text-gray-400 hover:text-white text-xs font-bold uppercase tracking-widest transition-colors duration-300">Telemetry</Link>
            </nav>
          </div>

          <div className="flex items-center space-x-3 md:space-x-6">
            <BackgroundGenerationIndicator />
            {user && (
              <Link to="/subscription" className="bg-white/5 border border-white/10 rounded-full px-3 md:px-4 py-1.5 flex items-center space-x-2 md:space-x-4 transition-all duration-500 hover:border-azure/30 group cursor-pointer relative overflow-hidden animate-fade-in text-[10px]">
                 <div className="flex flex-col text-right">
                    <span className="text-[7px] md:text-[8px] font-black uppercase tracking-[0.2em] text-gray-400 group-hover:text-azure-400 line-none">Tier</span>
                    <span className="text-azure-400 text-[8px] md:text-[10px] font-black uppercase tracking-widest">{subscriptionTier}</span>
                 </div>

                 {subscriptionTier !== 'Explorer' && expiryDate && (() => {
                    const isUrgent = new Date(expiryDate).getTime() - Date.now() < 24 * 60 * 60 * 1000;
                    return (
                        <div className="hidden lg:flex flex-col border-l border-white/10 pl-2 md:pl-4 text-right">
                            <span className={`text-[7px] md:text-[8px] font-black uppercase tracking-[0.2em] ${isUrgent ? 'text-orange-500 animate-pulse' : 'text-gray-500'}`}>
                                {isUrgent ? 'Expiry Imminent' : 'Expires'}
                            </span>
                            <span className={`text-[8px] md:text-[9px] font-black tracking-widest uppercase ${isUrgent ? 'text-orange-400' : 'text-white'}`}>
                                {new Date(expiryDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                            </span>
                        </div>
                    );
                 })()}
                 
                 {subscriptionTier === 'Standard' && (
                     <div className="flex flex-col border-l border-white/10 pl-2 md:pl-4">
                        <span className="text-[7px] md:text-[8px] font-black uppercase tracking-[0.2em] text-gray-400 group-hover:text-gold-500">Credits</span>
                        <span className="text-gold-500 text-[8px] md:text-[10px] font-black uppercase tracking-widest">{credits}</span>
                     </div>
                 )}
              </Link>
            )}

            {user && (
                <div ref={profileMenuRef} className="flex items-center gap-2 md:gap-4 border-l border-white/5 pl-3 md:pl-6 animate-fade-in text-[10px] relative">
                    <div className="text-right hidden md:block">
                        <p className="text-[10px] font-black text-white uppercase tracking-tighter truncate max-w-[80px] md:max-w-[100px]">{user.userDetails.split('@')[0]}</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setIsProfileMenuOpen(prev => !prev)}
                        className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-azure/20 border border-azure/30 flex items-center justify-center text-azure-400 font-black text-[10px] md:text-xs uppercase overflow-hidden cursor-pointer hover:border-azure transition-colors focus:outline-none"
                        aria-label="User Profile Menu"
                        aria-expanded={isProfileMenuOpen}
                    >
                        {user.persona?.anchorUrl ? <img src={user.persona.anchorUrl} alt="Avatar" className="w-full h-full object-cover" /> : user.userDetails[0]}
                    </button>
                    
                    {/* Responsive Profile Dropdown */}
                    <div className={`absolute right-0 top-full mt-4 w-48 py-2 bg-dk-900 border border-white/10 rounded-xl shadow-2xl transition-all duration-300 flex flex-col z-50 ${
                        isProfileMenuOpen 
                            ? 'opacity-100 visible pointer-events-auto translate-y-0' 
                            : 'opacity-0 invisible pointer-events-none -translate-y-2'
                    }`}>
                        <div className="px-4 py-2 border-b border-white/5 md:hidden">
                            <p className="text-[10px] font-black text-white uppercase tracking-tighter truncate">{user.userDetails.split('@')[0]}</p>
                        </div>
                        {user.persona?.description && (
                            <button onClick={() => { setIsProfileMenuOpen(false); setVaultInitialStep('review'); setShowIdentityVault(true); }} className="px-4 py-3 text-left text-[10px] font-bold text-azure-400 hover:bg-white/5 uppercase tracking-widest transition-colors w-full">View DNA Profile</button>
                        )}
                        <button onClick={() => { setIsProfileMenuOpen(false); setVaultInitialStep('capture'); setShowIdentityVault(true); }} className="px-4 py-3 text-left text-[10px] font-bold text-azure-400 hover:bg-white/5 uppercase tracking-widest transition-colors w-full">Sync Identity</button>
                        <button onClick={() => { setIsProfileMenuOpen(false); handleLogout(); }} className="px-4 py-3 text-left text-[10px] font-bold text-gray-400 hover:bg-white/5 hover:text-white uppercase tracking-widest transition-colors w-full text-left">Logout</button>
                        <div className="border-t border-white/5 my-1"></div>
                        <button 
                            onClick={() => { setIsProfileMenuOpen(false); handleExportData(); }} 
                            disabled={isExportingData}
                            className="px-4 py-3 text-left text-[10px] font-bold text-azure-400 hover:bg-white/5 uppercase tracking-widest transition-colors w-full flex items-center justify-between disabled:opacity-50"
                        >
                            <span>{isExportingData ? "Exporting Data..." : "Export Data (GDPR)"}</span>
                            <span className="text-[8px] bg-azure/20 text-azure px-1.5 py-0.5 rounded">JSON</span>
                        </button>
                        <button onClick={() => { setIsProfileMenuOpen(false); handleDeleteAccount(); }} className="px-4 py-3 text-left text-[10px] font-bold text-red-500 hover:bg-red-500/10 uppercase tracking-widest transition-colors w-full">Delete Account</button>
                    </div>
                </div>
            )}
          </div>
        </div>

        {/* Mobile Bottom Navigation - Accessible across all views */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-dk-900/90 backdrop-blur-xl border-t border-white/5 flex justify-around items-center p-3 safe-area-bottom animate-slide-up">
          <Link to="/" className="flex flex-col items-center gap-1 group relative">
            <MobileEngineIndicator />
            <div className="p-2 rounded-xl group-hover:bg-azure/10 transition-colors">
              <svg className="w-5 h-5 text-gray-400 group-hover:text-azure-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
            </div>
            <span className="text-xs font-black uppercase tracking-widest text-gray-400 group-hover:text-white transition-colors">Engine</span>
          </Link>
          {user && (
            <Link to="/subscription" className="flex flex-col items-center gap-1 group">
              <div className="p-2 rounded-xl group-hover:bg-azure/10 transition-colors">
                <svg className="w-5 h-5 text-gray-400 group-hover:text-azure-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0-2.08-.402-2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
              </div>
              <span className="text-xs font-black uppercase tracking-widest text-azure-400 group-hover:text-white transition-colors">Pricing</span>
            </Link>
          )}
          {user && (
            <Link to="/wardrobe" className="flex flex-col items-center gap-1 group">
              <div className="p-2 rounded-xl group-hover:bg-azure/10 transition-colors">
                <svg className="w-5 h-5 text-gray-400 group-hover:text-azure-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path></svg>
              </div>
              <span className="text-xs font-black uppercase tracking-widest text-gray-400 group-hover:text-white transition-colors">Closet</span>
            </Link>
          )}
          <Link to="/dashboard" className="flex flex-col items-center gap-1 group">
            <div className="p-2 rounded-xl group-hover:bg-azure/10 transition-colors">
              <svg className="w-5 h-5 text-gray-400 group-hover:text-azure-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 002 2h2a2 2 0 002-2z"></path></svg>
            </div>
            <span className="text-xs font-black uppercase tracking-widest text-gray-400 group-hover:text-white transition-colors">Telemetry</span>
          </Link>
        </nav>

        {/* Main Content Area */}
        <div className="flex-grow">
          <Routes>
            <Route path="/" element={<StylingEngine userId={user?.userId} subscriptionTier={subscriptionTier as any} onCreditUpdate={setCredits} />} />
            <Route path="/wardrobe" element={<WardrobeManager userId={user?.userId} subscriptionTier={subscriptionTier as any} />} />
            <Route 
              path="/subscription" 
              element={
                isInitializing ? (
                  <div className="min-h-[60vh] flex items-center justify-center">
                    <div className="w-8 h-8 border-2 border-azure-500/20 border-t-azure-500 rounded-full animate-spin"></div>
                  </div>
                ) : user ? (
                  <SubscriptionManager 
                    subscriptionTier={subscriptionTier as any} 
                    credits={credits} 
                    expiryDate={expiryDate} 
                    nextCycleTier={user?.nextCycleTier as any} 
                    gracePeriodDaysRemaining={gracePeriodDaysRemaining} 
                    isSoftLocked={isSoftLocked} 
                    userEmail={user?.email} 
                    userName={user?.userDetails} 
                  />
                ) : (
                  <SubscriptionAuthGate />
                )
              } 
            />
            <Route path="/payment-simulation" element={<PaymentSimulation onPaymentSuccess={handlePaymentSuccess} />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/dashboard" element={<Dashboard />} />
          </Routes>
        </div>

        {/* Global Footer */}
        <footer className="p-8 border-t border-white/5 flex flex-col items-center justify-center opacity-80 select-none">
            <div className="flex gap-6 mb-4 text-[9px] uppercase tracking-widest font-bold text-gray-500 flex-wrap justify-center">
                {user && (
                  <>
                    <Link to="/subscription" className="hover:text-azure-400 transition-colors text-azure-400 font-black">Pricing & Plans</Link>
                    <span className="text-white/10">|</span>
                  </>
                )}
                <Link to="/terms" className="hover:text-azure-400 transition-colors">Terms of Service</Link>
                <span className="text-white/10">|</span>
                <Link to="/dashboard" className="hover:text-azure-400 transition-colors">Telemetry Dashboard</Link>
                <span className="text-white/10">|</span>
                <button onClick={() => setShowFeedbackModal(true)} className="hover:text-azure-400 transition-colors flex items-center gap-1 cursor-pointer">💬 Send Feedback</button>
                <span className="text-white/10">|</span>
                <Link to="/privacy" className="hover:text-azure-400 transition-colors">Privacy Policy</Link>
            </div>
            <p className="text-[10px] uppercase tracking-[0.4em] font-medium text-gray-400 flex items-center">
               Powered By <DkpmoBrandLink />
            </p>
            <div className="mt-4 w-12 h-0.5 bg-azure-500/20 rounded-full"></div>
        </footer>
        <CookieBanner />
        </StylingProvider>
      </div>
    </>
  );
}

export default App;
